import { makeAuthenticatedRequest } from './authService';

// ========================================
// Types for Hindi Character Service
// ========================================

export interface CharacterVerificationRequest {
  image_data: string; // base64
  expected_character: string;
  mime_type: string;
}

export interface CharacterVerificationResult {
  is_correct: boolean;
  confidence_score: number; // 0-100
  feedback: string;
  suggestions?: string[];
}

export interface PracticeAttempt {
  id?: number;
  character: string;
  image_data: string;
  is_correct: boolean;
  score: number;
  feedback: string;
  created_at?: string;
}

export interface PracticeHistoryResponse {
  attempts: PracticeAttempt[];
  total: number;
  limit: number;
  offset: number;
}

export interface CharacterStats {
  character: string;
  total_attempts: number;
  correct_attempts: number;
  average_score: number;
  success_rate: number;
}

export interface CharacterStatsResponse {
  stats: CharacterStats[];
  overall_total: number;
  overall_correct: number;
  overall_average_score: number;
}

/**
 * Verify a drawn character using AI
 */
export const verifyCharacter = async (
  imageData: string,
  expectedCharacter: string
): Promise<CharacterVerificationResult> => {
  const response = await makeAuthenticatedRequest('/hindi/character/verify', {
    method: 'POST',
    body: JSON.stringify({
      image_data: imageData,
      expected_character: expectedCharacter,
      mime_type: 'image/png'
    })
  });

  if (!response.success) {
    throw new Error(response.message || response.error || 'Failed to verify character');
  }

  return response.data;
};

/**
 * Save a practice attempt
 */
export const savePracticeAttempt = async (
  attempt: Omit<PracticeAttempt, 'id' | 'created_at'>
): Promise<PracticeAttempt> => {
  const response = await makeAuthenticatedRequest('/hindi/character/practice', {
    method: 'POST',
    body: JSON.stringify(attempt)
  });

  if (!response.success) {
    throw new Error(response.message || response.error || 'Failed to save practice attempt');
  }

  return response.data;
};

/**
 * Get practice history
 */
export const getPracticeHistory = async (
  limit: number = 20,
  offset: number = 0,
  character?: string
): Promise<PracticeHistoryResponse> => {
  let endpoint = `/hindi/character/practice?limit=${limit}&offset=${offset}`;
  if (character) {
    endpoint += `&character=${encodeURIComponent(character)}`;
  }

  const response = await makeAuthenticatedRequest(endpoint, {
    method: 'GET'
  });

  if (!response.success) {
    throw new Error(response.message || response.error || 'Failed to get practice history');
  }

  return response.data;
};

/**
 * Get character statistics
 */
export const getCharacterStats = async (): Promise<CharacterStatsResponse> => {
  const response = await makeAuthenticatedRequest('/hindi/character/stats', {
    method: 'GET'
  });

  if (!response.success) {
    throw new Error(response.message || response.error || 'Failed to get character statistics');
  }

  return response.data;
};

